import {apiFetch} from "../client";
import type {SecondFactorStatus} from "./secondFactor";
import {parseJwt} from "../../utils/jwt";
import {listQuerySuffix, type ListParams} from "./common";

// ─── Onboarding domain (admin) ────────────────────────────────
// Источник правды по телам: internal/sso/dto.go,
// internal/sso/repository.go, internal/balance/team/*.
// POST /v1/auth/sign-up возвращает JwtResponse; id созданной сущности лежит
// в jwt.claims.reference_id. Сырые jwt/refreshToken в UI не передаем.

export type OnboardingAccountType = "merchant" | "trader";

export interface CreateTeamResponse {
    teamId: string;
    balanceId: string;
    name: string;
}

export interface TeamItem {
    id: string;
    name: string;
    balanceId: string;
    currency: string;
    amount: string;
    createdAt: string;
}

export interface TraderItem {
    id: string;
    /** id пользователя для двухфакторного входа (id — это трейдер). */
    userId?: string;
    login: string;
    name: string;
    description?: string;
    isActive: boolean;
    teamId?: string;
    teamName?: string;
    createdAt: string;
    secondFactor?: SecondFactorStatus;
}

export interface ListTeamsResponse {
    items: TeamItem[];
    limit: number;
    offset: number;
    hasMore: boolean;
    nextOffset?: number | null;
}

export interface ListTradersResponse {
    items: TraderItem[];
    limit: number;
    offset: number;
    hasMore: boolean;
    nextOffset?: number | null;
}

export interface AdminSignUpRequest {
    login: string;
    password: string;
    name: string;
    type: OnboardingAccountType;
    roles: OnboardingAccountType[];
    isActive: boolean;
    teamId?: string;
    isVip?: boolean;
}

export interface AdminSignUpResult {
    referenceId: string;
}

interface JwtResponse {
    jwt: string;
    refreshToken?: string;
}

/** POST /v1/team — создать команду с балансом. */
export function createTeam(token: string | null, name: string): Promise<CreateTeamResponse> {
    return apiFetch<CreateTeamResponse>("/team", token, {
        method: "POST",
        body: JSON.stringify({name}),
    });
}

/** GET /v1/team — admin-only список команд. */
export function listTeams(token: string | null, query: ListParams = {}): Promise<ListTeamsResponse> {
    return apiFetch<ListTeamsResponse>(`/team${listQuerySuffix(query)}`, token);
}

/** GET /v1/traders — admin-only список трейдеров с командой. */
export function listTraders(token: string | null, query: ListParams = {}): Promise<ListTradersResponse> {
    return apiFetch<ListTradersResponse>(`/traders${listQuerySuffix(query)}`, token);
}

/**
 * POST /v1/auth/sign-up — admin-only создание merchant/trader SSO.
 * Возвращаем только reference_id из JWT, потому что jwt/refreshToken принадлежат
 * новому аккаунту и не нужны admin UI.
 */
export async function adminSignUp(
    token: string | null,
    body: AdminSignUpRequest,
): Promise<AdminSignUpResult> {
    const resp = await apiFetch<JwtResponse>("/auth/sign-up", token, {
        method: "POST",
        body: JSON.stringify(body),
    });
    const payload = parseJwt(resp.jwt);
    if (!payload.reference_id) {
        throw new Error("JWT ответа sign-up не содержит reference_id.");
    }
    return {referenceId: payload.reference_id};
}
